import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Activity, Info, Clock, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import CountUp from "../components/CountUp";
import LendScoreGauge from "../components/LendScoreGauge";
import { useCachedApi, timeAgo } from "../lib/useCachedApi";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const Insights = () => {
    const { language, user } = useApp();
    const [data, setData] = useState(null);
    const uid = user?.user_id || "anon";
    const {
        data: aiData,
        generatedAt: aiGeneratedAt,
        loading: aiLoading,
        refreshing: aiRefreshing,
        refetch: aiRefetch,
    } = useCachedApi(
        `vlg:cache:${uid}:${language}:ai-insights`,
        "/ai/insights",
        { ttlMs: 6 * 60 * 60 * 1000 }
    );
    const aiInsights = aiData?.insights || [];

    useEffect(() => {
        api.get("/insights").then((r) => setData(r.data));
    }, []);

    return (
        <AppLayout title={t(language, "nav_insights")}>
            {!data ? (
                <div className="text-[hsl(var(--muted-foreground))]">Cargando...</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Card
                            className="rounded-3xl border-none relative overflow-hidden shadow-2xl text-white"
                            style={{
                                background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                            }}
                            data-testid="health-score-card"
                        >
                            {/* Neon glow accents */}
                            <div
                                className="absolute inset-0 pointer-events-none opacity-90"
                                style={{
                                    background:
                                        "radial-gradient(circle at 20% 15%, rgba(10,255,176,0.35) 0%, transparent 55%), radial-gradient(circle at 85% 85%, rgba(179,108,255,0.35) 0%, transparent 55%), radial-gradient(circle at 50% 100%, rgba(241,57,133,0.22) 0%, transparent 55%)",
                                }}
                            />
                            <div className="absolute inset-0 pointer-events-none grain opacity-30" />
                            <CardHeader className="relative">
                                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#0AFFB0] animate-pulse" />
                                    {t(language, "lendscore_title")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="relative">
                                <LendScoreGauge score={data.health_score} language={language} size={280} />
                                <p className="mt-4 text-sm text-white/90 leading-relaxed flex gap-2">
                                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#0AFFB0]" />
                                    <span>{t(language, "lendscore_desc")}</span>
                                </p>
                            </CardContent>
                        </Card>

                        <Card
                            className="rounded-3xl border-none relative overflow-hidden shadow-2xl text-white lg:col-span-2"
                            style={{
                                background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                            }}
                            data-testid="mom-card"
                        >
                            <div
                                className="absolute inset-0 pointer-events-none opacity-70"
                                style={{
                                    background:
                                        "radial-gradient(circle at 25% 20%, rgba(179,108,255,0.30) 0%, transparent 55%), radial-gradient(circle at 85% 90%, rgba(10,255,176,0.28) 0%, transparent 55%), radial-gradient(circle at 60% 40%, rgba(255,122,69,0.18) 0%, transparent 60%)",
                                }}
                            />
                            <div className="absolute inset-0 pointer-events-none grain opacity-30" />
                            <CardHeader className="relative">
                                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#B36CFF] animate-pulse" />
                                    {language === "es" ? "Comparativa mes vs mes" : "Month-over-month"}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 gap-6 relative">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-[#0AFFB0] font-bold">{language === "es" ? "Este mes" : "This month"}</div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/70">{language === "es" ? "Ingresos" : "Income"}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-white"><CountUp value={data.income_this} format={fmt} /></div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/70">{language === "es" ? "Gastos" : "Spending"}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-white"><CountUp value={data.spending_this} format={fmt} /></div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/70">{t(language, "savings_rate")}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-[#0AFFB0]"><CountUp value={data.savings_rate_this} format={(n) => `${n.toFixed(0)}%`} /></div>
                                    </div>
                                </div>
                                <div className="opacity-90">
                                    <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">{language === "es" ? "Mes pasado" : "Last month"}</div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/60">{language === "es" ? "Ingresos" : "Income"}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-white/85"><CountUp value={data.income_last} format={fmt} /></div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/60">{language === "es" ? "Gastos" : "Spending"}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-white/85"><CountUp value={data.spending_last} format={fmt} /></div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-sm text-white/60">{t(language, "savings_rate")}</div>
                                        <div className="font-display font-bold text-2xl tabular-nums text-white/85"><CountUp value={data.savings_rate_last} format={(n) => `${n.toFixed(0)}%`} /></div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <Card
                            className="rounded-3xl border-none relative overflow-hidden shadow-2xl text-white"
                            style={{
                                background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                            }}
                            data-testid="largest-purchases-card"
                        >
                            <div
                                className="absolute inset-0 pointer-events-none opacity-70"
                                style={{
                                    background:
                                        "radial-gradient(circle at 85% 15%, rgba(255,62,165,0.35) 0%, transparent 55%), radial-gradient(circle at 15% 85%, rgba(10,255,176,0.28) 0%, transparent 55%)",
                                }}
                            />
                            <div className="absolute inset-0 pointer-events-none grain opacity-30" />
                            <CardHeader className="relative">
                                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#FF3EA5]" />
                                    {language === "es" ? "Mayores compras" : "Largest purchases"}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="relative divide-y divide-white/10">
                                {data.largest_purchases.length === 0 && <div className="text-sm text-white/70 py-4">{t(language, "no_data")}</div>}
                                {data.largest_purchases.map((t2, i) => (
                                    <div key={i} className="flex items-center justify-between py-3">
                                        <div>
                                            <div className="font-medium text-white">{t2.merchant_name || t2.name}</div>
                                            <div className="text-xs text-white/70">{t2.date?.slice(0, 10)}</div>
                                        </div>
                                        <div className="font-bold tabular-nums text-[#0AFFB0]">{fmt(t2.amount)}</div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        <Card
                            className="rounded-3xl border-none relative overflow-hidden shadow-2xl text-white"
                            style={{
                                background: "linear-gradient(135deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                            }}
                            data-testid="caesar-insights-card"
                        >
                            <div
                                className="absolute inset-0 pointer-events-none opacity-80"
                                style={{
                                    background:
                                        "radial-gradient(circle at 15% 10%, rgba(10,255,176,0.35) 0%, transparent 55%), radial-gradient(circle at 90% 90%, rgba(179,108,255,0.4) 0%, transparent 55%), radial-gradient(circle at 55% 55%, rgba(241,57,133,0.18) 0%, transparent 60%)",
                                }}
                            />
                            <div className="absolute inset-0 pointer-events-none grain opacity-30" />
                            <CardHeader className="flex flex-row items-start justify-between gap-3 relative">
                                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#0AFFB0] animate-pulse" />
                                    {language === "es" ? "Insights de Caesar" : "Caesar's Insights"}
                                </CardTitle>
                                <div className="flex flex-col items-end gap-1">
                                    <button
                                        data-testid="refresh-insights-button"
                                        onClick={aiRefetch}
                                        disabled={aiRefreshing || aiLoading}
                                        aria-label={language === "es" ? "Refrescar insights" : "Refresh insights"}
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-opacity disabled:opacity-50 pill px-3 py-1.5 bg-white/15 hover:bg-white/25 border border-white/15"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${aiRefreshing ? "animate-spin" : ""}`} />
                                        {language === "es" ? "Refrescar" : "Refresh"}
                                    </button>
                                    {aiGeneratedAt && (
                                        <div data-testid="insights-freshness" className="inline-flex items-center gap-1 text-[10px] text-white/80">
                                            <Clock className="w-3 h-3" />
                                            {timeAgo(aiGeneratedAt, language)}
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3 relative">
                                {aiLoading && aiInsights.length === 0 && (
                                    <div className="flex items-center gap-2 text-sm text-white/85" data-testid="ai-insights-loading">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {language === "es" ? "Caesar está analizando tus datos…" : "Caesar is analyzing your data…"}
                                    </div>
                                )}
                                {!aiLoading && aiInsights.length === 0 && (
                                    <div className="text-sm text-white/85">
                                        {language === "es"
                                            ? "Aún no tenemos suficientes datos para generar insights. Vuelve pronto."
                                            : "Not enough data yet to generate insights. Come back soon."}
                                    </div>
                                )}
                                {aiInsights.map((ins, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: i * 0.1 }}
                                        data-testid="ai-insight-item"
                                        className="p-4 rounded-2xl bg-white/[0.08] backdrop-blur border border-white/10"
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Activity className="w-4 h-4 text-[#0AFFB0]" />
                                            <div className="font-semibold text-white">{ins.title}</div>
                                        </div>
                                        <div className="text-sm text-white/85 leading-relaxed">{ins.body}</div>
                                    </motion.div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </AppLayout>
    );
};

export default Insights;
